export type PipelineStatus = "shortlisted" | "contacted" | "in_discussion" | "proposal_sent" | "active" | "ended";

export const PIPELINE_STATUSES: { value: PipelineStatus; label: string; color: string }[] = [
  { value: "shortlisted", label: "Shortlisted", color: "220 10% 46%" },
  { value: "contacted", label: "Contacted", color: "217 65% 50%" },
  { value: "in_discussion", label: "In Discussion", color: "38 75% 50%" },
  { value: "proposal_sent", label: "Proposal Sent", color: "270 50% 55%" },
  { value: "active", label: "Active", color: "142 55% 38%" },
  { value: "ended", label: "Ended", color: "0 50% 45%" },
];

export type ListType = "campaign" | "project" | "interest";

export type CampaignStatus = "planning" | "ready_to_launch" | "live" | "completed";

export const CAMPAIGN_STATUSES: { value: CampaignStatus; label: string }[] = [
  { value: "planning", label: "Planning" },
  { value: "ready_to_launch", label: "Ready to Launch" },
  { value: "live", label: "Live" },
  { value: "completed", label: "Completed" },
];

export interface KeyContact {
  name: string;
  email: string;
}

export interface ActivityLogEntry {
  date: string;
  text: string;
}

export interface Milestone {
  id: string;
  name: string;
  date: string;
}

export interface ListCommunityEntry {
  id: string;
  communityId: string;
  communityName: string;
  theme: string;
  city: string;
  location: string;
  status: PipelineStatus;
  fitScore: number;
  activeMembers: number;
  reach: string;
  density: string;
  assignee: { name: string; initials: string };
  docs: number;
  notes: string;
  documents: { name: string; type: "file" | "url"; url?: string }[];
  keyContact?: KeyContact;
  activityLog?: ActivityLogEntry[];
}

export interface ManageList {
  id: string;
  name: string;
  listType: ListType;
  communities: ListCommunityEntry[];
  createdBy: { name: string; initials: string };
  createdDate: string;
  lastUpdated: string;
  // Campaign fields
  campaignBrief?: string;
  budget?: string;
  targetLaunchDate?: string;
  campaignStatus?: CampaignStatus;
  // Project fields
  objective?: string;
  teamMembers?: { name: string; initials: string }[];
  brainstormNotes?: string;
  milestones?: Milestone[];
  // Interest fields
  topicTheme?: string;
  freeNotes?: string;
  interestGroups?: { id: string; label: string }[];
}

export const manageLists: ManageList[] = [
  {
    id: "list-1",
    name: "Q2 Fitness Campaign",
    listType: "campaign",
    createdBy: { name: "Emma Laurent", initials: "EL" },
    createdDate: "2026-01-15",
    lastUpdated: "2026-03-02",
    campaignBrief: "Partner with fitness communities across France for Q2 product launch.",
    budget: "€25,000",
    targetLaunchDate: "2026-04-15",
    campaignStatus: "planning",
    communities: [
      {
        id: "entry-1", communityId: "paris-runners", communityName: "Paris Runners Club", theme: "Running", city: "Paris", location: "Paris, France",
        status: "active", fitScore: 92, activeMembers: 2400, reach: "45K", density: "840K", assignee: { name: "Emma Laurent", initials: "EL" },
        docs: 2, notes: "Great engagement on last campaign. Planning follow-up activation.", documents: [{ name: "Brief_Q2.pdf", type: "file" }],
        keyContact: { name: "Jean Dupont", email: "jean@parisrunners.fr" }, activityLog: [{ date: "2026-02-20", text: "Initial contact via email" }, { date: "2026-03-01", text: "Follow-up call — positive response" }],
      },
      {
        id: "entry-2", communityId: "yoga-paris", communityName: "Yoga Flow Paris", theme: "Yoga & Wellness", city: "Paris", location: "Paris, France",
        status: "ended", fitScore: 87, activeMembers: 1800, reach: "32K", density: "620K", assignee: { name: "Lucas Martin", initials: "LM" },
        docs: 1, notes: "Awaiting response on partnership proposal.", documents: [],
        keyContact: { name: "Claire Petit", email: "claire@yogaflow.fr" }, activityLog: [],
      },
      {
        id: "entry-3", communityId: "cycling-lyon", communityName: "Cycling Lyon", theme: "Cycling", city: "Lyon", location: "Lyon, France",
        status: "contacted", fitScore: 78, activeMembers: 950, reach: "18K", density: "380K", assignee: { name: "Emma Laurent", initials: "EL" },
        docs: 0, notes: "", documents: [],
      },
      {
        id: "entry-4", communityId: "hoka-fans", communityName: "HOKA Trail Crew", theme: "Trail Running", city: "Chamonix", location: "Chamonix, France",
        status: "shortlisted", fitScore: 94, activeMembers: 3200, reach: "65K", density: "1.2M", assignee: { name: "Lucas Martin", initials: "LM" },
        docs: 0, notes: "Top fit score — prioritize outreach.", documents: [],
      },
    ],
  },
  {
    id: "list-2",
    name: "Sustainable Brands Outreach",
    listType: "project",
    createdBy: { name: "Lucas Martin", initials: "LM" },
    createdDate: "2026-02-10",
    lastUpdated: "2026-03-04",
    objective: "Identify and partner with sustainability-focused communities for Earth Day initiative.",
    teamMembers: [{ name: "Lucas Martin", initials: "LM" }, { name: "Emma Laurent", initials: "EL" }],
    brainstormNotes: "Focus on Nantes & Marseille regions. Consider co-branded events. Explore artisan coffee angle.",
    milestones: [
      { id: "ms-1", name: "Research phase", date: "2026-02-15" },
      { id: "ms-2", name: "Outreach start", date: "2026-03-01" },
      { id: "ms-3", name: "Earth Day event", date: "2026-04-22" },
    ],
    communities: [
      {
        id: "entry-5", communityId: "sustainable-nantes", communityName: "Sustainable Nantes", theme: "Sustainability", city: "Nantes", location: "Nantes, France",
        status: "contacted", fitScore: 85, activeMembers: 1200, reach: "22K", density: "340K", assignee: { name: "Lucas Martin", initials: "LM" },
        docs: 1, notes: "Discussing co-branded event for Earth Day.", documents: [{ name: "Event Brief", type: "url", url: "https://example.com" }],
        keyContact: { name: "Marie Lefèvre", email: "marie@sustnantes.fr" }, activityLog: [{ date: "2026-03-02", text: "Sent partnership proposal" }],
      },
      {
        id: "entry-6", communityId: "coffee-marseille", communityName: "Coffee Lovers Marseille", theme: "Coffee & Artisan", city: "Marseille", location: "Marseille, France",
        status: "shortlisted", fitScore: 72, activeMembers: 680, reach: "12K", density: "140K", assignee: { name: "Emma Laurent", initials: "EL" },
        docs: 0, notes: "", documents: [],
      },
      {
        id: "entry-7", communityId: "street-art-marseille", communityName: "Street Art Marseille", theme: "Art & Culture", city: "Marseille", location: "Marseille, France",
        status: "ended", fitScore: 65, activeMembers: 520, reach: "9K", density: "190K", assignee: { name: "Lucas Martin", initials: "LM" },
        docs: 0, notes: "Declined — scope mismatch.", documents: [],
      },
    ],
  },
  {
    id: "list-3",
    name: "Tech Community Partners",
    listType: "project",
    createdBy: { name: "Emma Laurent", initials: "EL" },
    createdDate: "2026-02-28",
    lastUpdated: "2026-03-05",
    objective: "Build partnerships with tech & innovation communities in key French cities.",
    teamMembers: [{ name: "Emma Laurent", initials: "EL" }],
    brainstormNotes: "",
    milestones: [],
    communities: [
      {
        id: "entry-8", communityId: "tech-bordeaux", communityName: "Tech Meetup Bordeaux", theme: "Tech & Innovation", city: "Bordeaux", location: "Bordeaux, France",
        status: "contacted", fitScore: 88, activeMembers: 1500, reach: "28K", density: "520K", assignee: { name: "Emma Laurent", initials: "EL" },
        docs: 1, notes: "Initial contact made via LinkedIn.", documents: [],
        keyContact: { name: "Thomas Bernard", email: "thomas@techbdx.fr" }, activityLog: [{ date: "2026-03-04", text: "LinkedIn message sent" }],
      },
      {
        id: "entry-9", communityId: "entrepreneurs-paris", communityName: "Entrepreneurs Paris", theme: "Business", city: "Paris", location: "Paris, France",
        status: "active", fitScore: 81, activeMembers: 2100, reach: "40K", density: "1.5M", assignee: { name: "Lucas Martin", initials: "LM" },
        docs: 0, notes: "Call scheduled for March 10.", documents: [],
      },
    ],
  },
  {
    id: "list-4",
    name: "Interesting Communities",
    listType: "interest",
    createdBy: { name: "Emma Laurent", initials: "EL" },
    createdDate: "2026-03-01",
    lastUpdated: "2026-03-05",
    topicTheme: "Diverse communities to watch",
    freeNotes: "Keep an eye on emerging communities in wellness and culture spaces.",
    interestGroups: [
      { id: "ig-1", label: "Running" },
      { id: "ig-2", label: "Culture" },
      { id: "ig-3", label: "To explore" },
    ],
    communities: [
      {
        id: "entry-10", communityId: "photography-paris", communityName: "Photography Paris", theme: "Photography", city: "Paris", location: "Paris, France",
        status: "shortlisted", fitScore: 76, activeMembers: 900, reach: "15K", density: "280K", assignee: { name: "Emma Laurent", initials: "EL" },
        docs: 0, notes: "Interesting visual content potential.", documents: [],
      },
      {
        id: "entry-11", communityId: "book-club-toulouse", communityName: "Book Club Toulouse", theme: "Literature", city: "Toulouse", location: "Toulouse, France",
        status: "shortlisted", fitScore: 68, activeMembers: 450, reach: "8K", density: "120K", assignee: { name: "Emma Laurent", initials: "EL" },
        docs: 0, notes: "", documents: [],
      },
    ],
  },
];
